import './styles.css';
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ConfigProvider, Skeleton, Input, Popconfirm, Empty } from 'antd';
import {
    ArrowLeftOutlined,
    CreditCardOutlined,
    CloseCircleOutlined,
    MessageOutlined,
    SendOutlined,
    EnvironmentOutlined,
    ShoppingOutlined,
    UserOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import { toastr } from 'react-redux-toastr';
import { ordersSelectors, fetchOrder, ordersApi } from '../../features/orders';

const { TextArea } = Input;

const STATUS_META = {
    'pending': { label: 'Ожидает оплаты', color: '#faad14', bg: 'rgba(250, 173, 20, 0.15)', border: 'rgba(250, 173, 20, 0.3)' },
    'processing': { label: 'В обработке', color: '#1890ff', bg: 'rgba(24, 144, 255, 0.15)', border: 'rgba(24, 144, 255, 0.3)' },
    'on-hold': { label: 'На удержании', color: '#d4a017', bg: 'rgba(212, 160, 23, 0.15)', border: 'rgba(212, 160, 23, 0.3)' },
    'completed': { label: 'Выполнен', color: '#52c41a', bg: 'rgba(82, 196, 26, 0.15)', border: 'rgba(82, 196, 26, 0.3)' },
    'cancelled': { label: 'Отменён', color: '#ff4d4f', bg: 'rgba(208, 2, 2, 0.15)', border: 'rgba(208, 2, 2, 0.3)' },
    'refunded': { label: 'Возвращён', color: '#ff7a45', bg: 'rgba(255, 122, 69, 0.15)', border: 'rgba(255, 122, 69, 0.3)' },
    'failed': { label: 'Не удался', color: '#ff4d4f', bg: 'rgba(208, 2, 2, 0.15)', border: 'rgba(208, 2, 2, 0.3)' },
};

const PAYMENT_LABELS = {
    'cheque': 'Оплата частями / в кредит',
    'tinkoff': 'Онлайн-оплата картой',
    'bacs': 'Оплата по счёту',
    'cod': 'Оплата при получении',
};

const CANCELLABLE = ['pending', 'on-hold', 'processing'];

const formatPrice = (value) => `${Math.round(Number(value) || 0).toLocaleString('ru-RU')}₽`;

const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getMeta = (order, key) => {
    const entry = (order?.meta_data || []).find((m) => m.key === key);
    return entry ? entry.value : null;
};

const shippingTitle = (line) => {
    if (!line) return null;
    if ((line.method_id || '').startsWith('own::SHIPPING_TYPE_LOCAL_PICKUP')) {
        return 'Самовывоз';
    }
    return line.method_title || line.method_id;
};

const renderAddress = (addr) => {
    if (!addr) return null;
    const name = [addr.first_name, addr.last_name].filter(Boolean).join(' ');
    const lines = [
        name,
        addr.company,
        [addr.postcode, addr.country].filter(Boolean).join(', '),
        addr.state,
        addr.city,
        addr.address_1,
        addr.address_2,
        addr.phone,
        addr.email,
    ].filter(Boolean);
    return lines;
};

const OrderDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { orderId } = useParams();

    const order = useSelector(ordersSelectors.selectLastOrder);
    const cachedPaymentUrl = useSelector((state) => ordersSelectors.selectPaymentUrl(state, orderId));
    const token = useSelector((state) => state.auth?.token);

    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const loadNotes = useCallback(async () => {
        if (!orderId) return;
        setNotesLoading(true);
        try {
            const result = await ordersApi.fetchOrderNotes(orderId);
            setNotes(Array.isArray(result) ? result : []);
        } catch (error) {
            console.error('Fetch notes error:', error);
        } finally {
            setNotesLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        if (!orderId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        dispatch(fetchOrder(orderId)).finally(() => setLoading(false));
        loadNotes();
    }, [dispatch, orderId, loadNotes]);

    const matchedOrder = order && String(order.id) === String(orderId) ? order : null;
    const status = matchedOrder?.status;
    const statusMeta = STATUS_META[status] || { label: status, color: 'rgba(255,255,255,0.7)', bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.15)' };
    const needsPayment = status === 'pending' || status === 'on-hold';
    const paymentUrl = cachedPaymentUrl || matchedOrder?.payment_url || null;

    const handleAddNote = useCallback(async () => {
        if (!newNote.trim()) return;
        setAddingNote(true);
        try {
            await ordersApi.addOrderNote(orderId, newNote.trim());
            setNewNote('');
            await loadNotes();
            toastr.success('Успешно', 'Заметка добавлена');
        } catch (error) {
            console.error('Add note error:', error);
            toastr.error('Ошибка', 'Не удалось добавить заметку');
        } finally {
            setAddingNote(false);
        }
    }, [newNote, orderId, loadNotes]);

    const handleCancel = useCallback(async () => {
        setCancelling(true);
        try {
            await ordersApi.cancelOrder(orderId, token);
            toastr.success('Успешно', 'Заказ отменён');
            dispatch(fetchOrder(orderId));
        } catch (error) {
            console.error('Cancel order error:', error);
            toastr.error('Ошибка', 'Не удалось отменить заказ');
        } finally {
            setCancelling(false);
        }
    }, [orderId, token, dispatch]);

    const handlePay = useCallback(() => {
        if (paymentUrl) {
            window.location.href = paymentUrl;
        }
    }, [paymentUrl]);

    if (loading) {
        return (
            <div className="order-detail-page">
                <div className="order-detail-container">
                    <div className="order-detail-panel">
                        <Skeleton active paragraph={{ rows: 2 }} title={{ width: '40%' }} />
                    </div>
                    <div className="order-detail-panel">
                        <Skeleton active paragraph={{ rows: 5 }} title={false} />
                    </div>
                </div>
            </div>
        );
    }

    if (!matchedOrder) {
        return (
            <div className="order-detail-page">
                <div className="order-detail-container">
                    <div className="order-detail-panel order-detail-empty">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<span className="order-detail-empty-text">Заказ не найден или недоступен</span>}
                        />
                        <Button type="primary" onClick={() => navigate('/profile')}>
                            К моим заказам
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const billing = matchedOrder.billing || {};
    const shipping = matchedOrder.shipping || {};
    const items = matchedOrder.line_items || [];
    const shippingLine = (matchedOrder.shipping_lines || [])[0];
    const pickupPoint = getMeta(matchedOrder, 'pickup_point');
    const tracking = getMeta(matchedOrder, 'tracking_info');
    const customerNote = matchedOrder.customer_note;

    const itemsSubtotal = items.reduce((sum, it) => sum + (Number(it.total) || 0), 0);
    const discount = Number(matchedOrder.discount_total) || 0;
    const shippingTotal = Number(matchedOrder.shipping_total) || 0;
    const orderNumber = matchedOrder.number || matchedOrder.order_number || matchedOrder.id;
    const paymentLabel = matchedOrder.payment_method_title
        || PAYMENT_LABELS[matchedOrder.payment_method]
        || matchedOrder.payment_method
        || '—';

    const billingLines = renderAddress(billing);
    const shippingLines = renderAddress(shipping);

    return (
        <ConfigProvider
            theme={{
                token: { colorPrimary: '#D00202' },
                components: { Button: { colorPrimaryHover: '#ff3333' } },
            }}
        >
            <div className="order-detail-page">
                <div className="order-detail-container">
                    <button type="button" className="order-detail-back" onClick={() => navigate('/profile')}>
                        <ArrowLeftOutlined /> Мои заказы
                    </button>

                    <header className="order-detail-header">
                        <div className="order-detail-heading">
                            <h1 className="order-detail-number">Заказ #{orderNumber}</h1>
                            <span className="order-detail-date">{formatDateTime(matchedOrder.date_created)}</span>
                        </div>
                        <span
                            className="order-detail-status"
                            style={{ color: statusMeta.color, background: statusMeta.bg, borderColor: statusMeta.border }}
                        >
                            {statusMeta.label}
                        </span>
                    </header>

                    {(needsPayment && paymentUrl) || CANCELLABLE.includes(status) ? (
                        <div className="order-detail-actions-bar">
                            {needsPayment && paymentUrl && (
                                <Button
                                    type="primary"
                                    icon={<CreditCardOutlined />}
                                    onClick={handlePay}
                                    className="order-detail-pay-btn"
                                >
                                    Оплатить {formatPrice(matchedOrder.total)}
                                </Button>
                            )}
                            {CANCELLABLE.includes(status) && (
                                <Popconfirm
                                    title="Отменить заказ?"
                                    description="Это действие нельзя отменить"
                                    onConfirm={handleCancel}
                                    okText="Да, отменить"
                                    cancelText="Нет"
                                    overlayClassName="dark-popconfirm"
                                >
                                    <Button icon={<CloseCircleOutlined />} loading={cancelling} className="order-detail-cancel-btn">
                                        Отменить заказ
                                    </Button>
                                </Popconfirm>
                            )}
                        </div>
                    ) : null}

                    <section className="order-detail-panel">
                        <div className="order-detail-panel-title">
                            <ShoppingOutlined /> Состав заказа
                        </div>
                        <div className="order-items">
                            {items.length === 0 && <div className="order-detail-muted">Нет позиций</div>}
                            {items.map((item) => {
                                const clickable = !!item.product_id;
                                const openProduct = () => clickable && navigate(`/product/${item.product_id}`);
                                return (
                                    <div
                                        className={`order-item-row${clickable ? ' order-item-clickable' : ''}`}
                                        key={item.id || item.name}
                                        role={clickable ? 'link' : undefined}
                                        tabIndex={clickable ? 0 : undefined}
                                        onClick={openProduct}
                                        onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openProduct(); } }}
                                    >
                                        <div className="order-item-thumb">
                                            {item.image && item.image.src
                                                ? <img src={item.image.src} alt={item.name} loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                                : <ShoppingOutlined />}
                                        </div>
                                        <div className="order-item-info">
                                            <span className="order-item-name">{item.name}</span>
                                            <span className="order-item-sku">
                                                {item.sku ? `арт. ${item.sku} · ` : ''}{formatPrice(item.price)} / шт.
                                            </span>
                                        </div>
                                        <span className="order-item-qty">{item.quantity} шт.</span>
                                        <span className="order-item-total">{formatPrice(item.total)}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="order-totals">
                            <div className="order-total-row">
                                <span>Товары</span>
                                <span>{formatPrice(itemsSubtotal)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="order-total-row">
                                    <span>Скидка</span>
                                    <span className="order-total-discount">−{formatPrice(discount)}</span>
                                </div>
                            )}
                            <div className="order-total-row">
                                <span>Доставка</span>
                                <span>{shippingTotal > 0 ? formatPrice(shippingTotal) : 'бесплатно'}</span>
                            </div>
                            <div className="order-total-row order-total-grand">
                                <span>Итого</span>
                                <span>{formatPrice(matchedOrder.total)}</span>
                            </div>
                        </div>
                    </section>

                    <section className="order-detail-panel">
                        <div className="order-detail-panel-title">
                            <InboxOutlined /> Доставка и оплата
                        </div>
                        <div className="order-detail-rows">
                            <div className="order-detail-row">
                                <span className="order-detail-label">Способ доставки</span>
                                <span className="order-detail-value">{shippingTitle(shippingLine) || '—'}</span>
                            </div>
                            {pickupPoint && (pickupPoint.company || pickupPoint.address) && (
                                <div className="order-detail-row">
                                    <span className="order-detail-label">Пункт выдачи</span>
                                    <span className="order-detail-value order-detail-value-multiline">
                                        {pickupPoint.company && <span>{pickupPoint.company}</span>}
                                        {pickupPoint.address && <span className="order-detail-muted">{pickupPoint.address}</span>}
                                    </span>
                                </div>
                            )}
                            {tracking && tracking.track_code && (
                                <div className="order-detail-row">
                                    <span className="order-detail-label">Трек-номер</span>
                                    <span className="order-detail-value">
                                        {tracking.tracking_link
                                            ? <a href={tracking.tracking_link} target="_blank" rel="noreferrer">{tracking.track_code}</a>
                                            : tracking.track_code}
                                    </span>
                                </div>
                            )}
                            <div className="order-detail-row">
                                <span className="order-detail-label">Способ оплаты</span>
                                <span className="order-detail-value">{paymentLabel}</span>
                            </div>
                        </div>
                    </section>

                    <div className="order-detail-address-grid">
                        <section className="order-detail-panel">
                            <div className="order-detail-panel-title">
                                <UserOutlined /> Плательщик
                            </div>
                            <div className="order-address">
                                {billingLines && billingLines.length
                                    ? billingLines.map((line, i) => <span key={i}>{line}</span>)
                                    : <span className="order-detail-muted">Не указан</span>}
                            </div>
                        </section>

                        <section className="order-detail-panel">
                            <div className="order-detail-panel-title">
                                <EnvironmentOutlined /> Адрес доставки
                            </div>
                            <div className="order-address">
                                {shippingLines && shippingLines.length
                                    ? shippingLines.map((line, i) => <span key={i}>{line}</span>)
                                    : <span className="order-detail-muted">Совпадает с плательщиком</span>}
                            </div>
                        </section>
                    </div>

                    {customerNote && (
                        <section className="order-detail-panel">
                            <div className="order-detail-panel-title">Комментарий к заказу</div>
                            <p className="order-customer-note">{customerNote}</p>
                        </section>
                    )}

                    <section className="order-detail-panel">
                        <div className="order-detail-panel-title">
                            <MessageOutlined /> Заметки
                        </div>
                        <div className="order-notes">
                            {notesLoading ? (
                                <Skeleton active paragraph={{ rows: 2 }} title={false} />
                            ) : notes.length === 0 ? (
                                <div className="order-detail-muted">Заметок пока нет</div>
                            ) : (
                                notes.map((item, idx) => (
                                    <div className="order-note-item" key={item.id || idx}>
                                        <div className="order-note-text" dangerouslySetInnerHTML={{ __html: item.note }} />
                                        <div className="order-note-meta">
                                            {item.date_created ? new Date(item.date_created).toLocaleString('ru-RU') : ''}
                                            {item.author ? ` — ${item.author}` : ''}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="order-notes-input">
                            <TextArea
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                placeholder="Написать заметку к заказу..."
                                autoSize={{ minRows: 2, maxRows: 5 }}
                            />
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={handleAddNote}
                                loading={addingNote}
                                disabled={!newNote.trim()}
                                className="order-notes-send"
                            >
                                Отправить
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default OrderDetail;
